import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, SxProps, Theme } from '@mui/material/styles';
import { TABLE_FONT_FAMILY } from '../../theme';
import { NAUTILUS, useConsolePalette } from './consolePalette';

type ConsolePanelProps = {
  title: string;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  bodySx?: SxProps<Theme>;
};

export const ConsolePanel: React.FC<ConsolePanelProps> = ({
  title,
  subtitle,
  icon,
  action,
  children,
  bodySx,
}) => {
  const palette = useConsolePalette();

  return (
    <Box
      component="section"
      aria-label={title}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        border: `1px solid ${palette.border}`,
        borderRadius: 1,
        bgcolor: palette.surface,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${palette.border}`,
          bgcolor: palette.header,
          minHeight: 56,
        }}
      >
        {icon && <PanelIcon>{icon}</PanelIcon>}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            component="h2"
            sx={{
              fontSize: '0.8rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: palette.ink,
              lineHeight: 1.3,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" component="div" sx={{ color: palette.inkSoft, lineHeight: 1.4 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action && <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 2 }}>{action}</Box>}
      </Box>
      <Box sx={[{ flex: 1, minHeight: 0, p: 2 }, ...(Array.isArray(bodySx) ? bodySx : [bodySx])]}>
        {children}
      </Box>
    </Box>
  );
};

export const PanelIcon: React.FC<{ children: React.ReactNode; size?: number }> = ({ children, size = 30 }) => {
  const palette = useConsolePalette();
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        borderRadius: 1,
        bgcolor: palette.isDark ? alpha(NAUTILUS.sand, 0.1) : alpha(NAUTILUS.deepOcean, 0.06),
        color: palette.figure,
        '& svg': { fontSize: size * 0.6 },
      }}
    >
      {children}
    </Box>
  );
};

export const PanelStat: React.FC<{ label: string; value: React.ReactNode; align?: 'left' | 'right' }> = ({
  label,
  value,
  align = 'right',
}) => {
  const palette = useConsolePalette();
  return (
    <Box sx={{ textAlign: align }}>
      <Typography
        sx={{
          fontFamily: TABLE_FONT_FAMILY,
          fontWeight: 700,
          fontSize: '1rem',
          lineHeight: 1.2,
          color: palette.figure,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: palette.inkFaint, whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
    </Box>
  );
};

type PillTone = 'sand' | 'slate' | 'starting' | 'buoy';

export const StatusPill: React.FC<{ label: string; tone: PillTone }> = ({ label, tone }) => {
  const palette = useConsolePalette();
  const tones: Record<PillTone, { bg: string; fg: string }> = {
    sand: {
      bg: alpha(NAUTILUS.sand, palette.isDark ? 0.18 : 0.32),
      fg: palette.isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
    },
    slate: { bg: alpha(NAUTILUS.slate, palette.isDark ? 0.3 : 0.14), fg: palette.inkSoft },
    starting: { bg: palette.starting, fg: palette.startingInk },
    buoy: { bg: alpha(NAUTILUS.buoy, palette.isDark ? 0.2 : 0.14), fg: palette.isDark ? '#f0a35e' : '#b35a10' },
  };
  const { bg, fg } = tones[tone];

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: 1,
        py: 0.25,
        borderRadius: 0.75,
        bgcolor: bg,
        color: fg,
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
        lineHeight: 1.6,
      }}
    >
      {label}
    </Box>
  );
};

export const useConsoleTableSx = () => {
  const palette = useConsolePalette();
  return {
    '& .MuiTableCell-root': {
      borderBottom: `1px solid ${palette.rule}`,
      fontSize: '0.8rem',
      py: 1,
      px: 1.5,
      color: palette.ink,
    },
    '& .MuiTableCell-head': {
      bgcolor: palette.surface,
      color: palette.inkFaint,
      fontSize: '0.7rem',
      fontWeight: 600,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      borderBottom: `1px solid ${palette.border}`,
    },
    '& .MuiTableRow-hover': { cursor: 'pointer' },
    '& .MuiTableRow-hover:hover': { bgcolor: `${palette.hover} !important` },
    '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': { borderBottom: 'none' },
  } as const;
};

export const EmptyNote: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const palette = useConsolePalette();
  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 96,
        display: 'grid',
        placeItems: 'center',
        textAlign: 'center',
        px: 2,
        border: `1px dashed ${palette.border}`,
        borderRadius: 1,
      }}
    >
      <Typography variant="body2" sx={{ color: palette.inkSoft, maxWidth: 360 }}>
        {children}
      </Typography>
    </Box>
  );
};
